import {Injectable} from "@angular/core";
import {StoreSelectorsService} from "./store-selectors.service";
import {actionGetLocalStorageConfigServer, actionGetLocalStorageConfigServerReply, actionPostChatArchiveReply, actionPreloginResellerRequest, actionSetLocalStorageConfig, localStorageUpdates} from "../store/actions/user.actions";
import {BehaviorSubject} from "rxjs";
import {EnumStudioType, ExperienceLevelType, ICartCreate, ISharedResourceLibrary, StringBinary} from "../interfaces";
import {DEFAULT_TEMPLATE_ID, isNullOrUndefOrEmpty} from "../common";
import {Lib} from "../../libs/Lib";
import * as _ from 'lodash';
import {once} from "../decorators/once-decorator";
import {ofType} from "@ngrx/effects";
import {ActionsSubject} from "@ngrx/store";
import {aiMapping} from "./ai/definitions/aiMapping";

// sessionStorage flag marking an autologin/sub-account tab as ephemeral, so it stays sessionStorage
// backed across reloads after ?autologin= is stripped from the URL. sessionStorage is per-tab and
// cleared on tab close, so it never affects the reseller tab or a fresh studio.
const EPHEMERAL_SESSION_MARKER = 'ephemeralStudioSession';

const {version: version} = require('../../../package.json');
const {localStoreConfigVersion: localStoreConfigVersion} = require('../../../package.json');
const {localStoreSharedLibraryVersion: localStoreSharedLibraryVersion} = require('../../../package.json');

export type ILocalStoreTypes =
    'ICartCreate'
    | 'IUserWork'
    | 'ILocalStorageConfig'
    | 'IPreloginReseller'
    | 'ISharedResourceLibrary'
    | 'IUser'
    | 'CLEAR'
    | 'FASTERQ';

export interface IPreloginReseller {
    resellerId: string;
}

interface IConfigItem {
    key: string;
    val: any;
}

export interface ILocalStorageConfig {
    version: string;
    studioId: string;
    noLoginUserName: string;
    sceneFilter: string;
    painterFilter: string;
    sceneCategory: string;
    painterCategory: string;
    stationFilter: string;
    dropbox_key: string;
    localStoreConfigVersion: string;
    defaultLanguage: string;
    sceneOpacityLevel: string;
    snapScale: string;
    sceneCheckeredBackground: string;
    sceneGuidelinesOpacityLevel: string;
    targetPlayer: string;
    fasterqEnabled: string;
    showHelpEnabled: string;
    advancedScheduler: string;
    eventsEnabled: string;
    catalogEnabled: string;
    transitionsEnabled: string;
    musicEnabled: string;
    dashboardEnabled: string;
    scheduledEnabled: string;
    resurcesEnabled: string;
    paintEnabled: string;
    theme: string;
    useAvatar: string;
    billingAsShipping: StringBinary;
    largeLogo: string;
    showExperienceLevel: string;
    experienceLevel: ExperienceLevelType;
    watchedVideoLinks: object;
    socialUserPendingStudioType: EnumStudioType;
    socialUserPendingTemplateId: string;
    pwaHideInstaller: string;
    privateServer: string;
    onSaleShown: string;
    ldapServer: string;
    saveSceneTimer: string;
    saveSceneManual: string;
    showDirections: string;
    stickersAsEmbedded: string;
    scriptingMode: string;
    saveFullFeature: string;
    restartPlayer: string;
    restartPlayerOnSceneAssignment: string;
    accountInitialCopyResources: string;
    enableDashboard: string;
    guidelines: string;
    favoriteFonts: string;
    isWhitelabel: string;
    isYouTubeLicense: string;
    pageSize: string;

    // Monitor Wall v2 feature flag (roadmap M1..M7). When truthy, enterprise
    // users see the new Monitor Wall entry point. Remove once M7 lands.
    monitorWallV2: string;

    // custom size of new scene wizard
    dimensionIndex: string;
    customSizeX: string;
    customSizeY: string;

    // config of preview scene mode
    previewMode: string;
    previewPercent: string;
    previewSizeX: string;
    previewSizeY: string;
    previewSceneId: string;
    rememberPreviewSettings: string;

    // scene editor zoom
    zoom: string;
    timelineZoom: string;
    playlistScenePreviewEnabled: string;
    recentScenePreviewEnabled: string;

    // saveSceneOnExit: string;
    playersFilter: string;
    playersConnected: string;
    playersStatsChartEnabled: string;
    templateCategories: string;
    resourceCategories: string;
    stationMonitorUrl: string;

    // police
    equipmentPwa: string;
    equipmentUser: string;
    equipmentEmail: string;
    equipmentId: string;
    equipmentSlider: string;
    equipmentAccess: string;

    ai: {
        chat?: any;
        createImage?: any;
        editImage?: any;
        createVideo?: any;
        editVideo?: any;
        autoHide?: boolean;
        showThinking?: boolean;
        lastScenePrompt?: string;  // Last AI scene wizard prompt
        sceneHistory?: Array<{ prompt: string; timestamp: number; sceneSummary: string; enhancements: any }>;
    };
    slideerSidebarMinimized: string;
    selectedColorPalette: string;
    useDevServerPort: string;
    iotFilterRegistered: string;
    pinnedModules: string;
    /** Per-section pin + minimize preferences for the home page. JSON
     *  string of `{ [sectionId]: { pinned?: boolean; minimized?: boolean } }`.
     *  Read/written by app-home-section. Stored as a flat string so the
     *  existing string-only setStorageValConfig path keeps working. */
    homeSectionPrefs: string;
}

@Injectable()
export class LocalStoreService {

    theme = new BehaviorSubject(null);

    /**
     * Per-tab storage backend. Autologin tabs ("open studio as this user" via ?autologin=) use
     * sessionStorage so the impersonated sub-account's settings, cart, shared library and user are
     * isolated to that tab and never share or clobber the reseller tab's localStorage on the same
     * origin (both would otherwise collide on ILocalStorageConfig, ICartCreate, logout wipes, etc.).
     * Regular tabs keep using localStorage so sessions persist across restarts as before.
     */
    private store: Storage = window.localStorage;

    constructor(private storeSelector: StoreSelectorsService, private actionsSubj: ActionsSubject) {
        this.store = this.pickStore();
        const storage = this.getStorageConfig();
        if (storage && storage.localStoreConfigVersion !== localStoreConfigVersion) {
            this.removeConfigStorage();
        }
        this.setDefaultLocalConfigStorage();
        this.listenLocalStorageConfigSync();
    }

    /** autologin tabs are isolated to sessionStorage (see `store`); everything else uses localStorage */
    private pickStore(): Storage {
        try {
            const hasParam = new URLSearchParams(window.location.search).has('autologin');
            if (hasParam) {
                // mark this tab ephemeral so it stays sessionStorage-backed across a reload even after
                // login.component scrubs ?autologin= from the URL (the param no longer survives reload,
                // but sessionStorage does — and it's per-tab, so the reseller tab is unaffected)
                window.sessionStorage.setItem(EPHEMERAL_SESSION_MARKER, '1');
            }
            if (hasParam || window.sessionStorage.getItem(EPHEMERAL_SESSION_MARKER) === '1') {
                return window.sessionStorage;
            }
        } catch (e) {
            // location/URLSearchParams/sessionStorage unavailable — fall back to localStorage
        }
        return window.localStorage;
    }

    /** true when this tab is an autologin/sub-account session backed by sessionStorage (per-tab isolated) */
    isEphemeralSession(): boolean {
        return this.store === window.sessionStorage;
    }

    listenLocalStorageConfigSync() {
        this.actionsSubj.pipe(ofType(actionGetLocalStorageConfigServerReply))
            .subscribe((reply) => {
                const localStorageConfigRemote: ILocalStorageConfig = reply.localStoreConfig;
                const studioIdRemote = localStorageConfigRemote?.studioId;
                if (isNullOrUndefOrEmpty(studioIdRemote)) return;

                /* if local studioId === remote studioId, no reason to sync as user never logged out of studio */
                // if (studioIdLocal === studioIdRemote) return;
                /** merge every remote key into a single local write: a per key setStorageValConfig() re-parsed the
                 *  whole blob and dispatched localStorageUpdates once per key (about 100 times) on every login **/
                const storage: ILocalStorageConfig = this.getStorageConfig();
                if (!storage) return;
                const studioIdLocal = storage.studioId;
                Object.keys(localStorageConfigRemote).forEach((k) => {
                    let config = k === 'localStoreConfigVersion' ? localStoreConfigVersion : localStorageConfigRemote[k];
                    if (k === 'studioId') {
                        config = studioIdLocal || Lib.RandomString();
                    }
                    storage[k] = config;
                });
                this.setStorageConfig(storage);
                /** back fill any defaults the remote copy is missing, same as the per key path used to do **/
                this.setDefaultLocalConfigStorage();
                if ('theme' in localStorageConfigRemote) {
                    this.changeTheme(storage.theme);
                }
                console.log('sync localstorage to remote config');
                this.setRemoteLocalStorageConfig();
            });
    }

    dispatchLocalUpdated(event: ILocalStoreTypes) {
        this.storeSelector.appStore.dispatch(localStorageUpdates({
            localStorageUpdates: {
                latestEvent: event,
                versionUpdate: Number(_.uniqueId())
            }
        }));
    }

    getItem(key: ILocalStoreTypes, defaultValue?: any, expiry?: boolean): any {
        const item = this.store.getItem(key);
        if (!item) return defaultValue;
        const data: any = JSON.parse(item);
        if (expiry) {
            const now = new Date();
            if (data?.expiry && now.getTime() > data?.expiry) {
                /** if the item is expired delete the item from storage and return defaultValue or null **/
                this.removeItem(key);
                return defaultValue ? defaultValue : null;
            } else {
                return data;
            }
        } else {
            return data;
        }
    }

    setItem(key: ILocalStoreTypes, value, ttl_in_minutes?: number): void {
        if (ttl_in_minutes) {
            const ttl_in_milliseconds = ttl_in_minutes * 1000 * 60;
            const now = new Date();
            value['expiry'] = now.getTime() + ttl_in_milliseconds;
        }
        this.store.setItem(key, JSON.stringify(value));
        this.dispatchLocalUpdated(key);
    }

    /**
     * Mirror the local config to the server (dwSignSetGlobalConfig).
     * @param immediate cancel any push in flight and send now (see setLocalStoreConfigEffect$), used by logout() which navigates away right after
     */
    setRemoteLocalStorageConfig(immediate = false) {
        const val: string = this.store.getItem('ILocalStorageConfig');
        /** never push an empty snapshot: clearStorage() followed by logout() would overwrite the remote copy with null **/
        if (isNullOrUndefOrEmpty(val)) return;
        this.storeSelector.dispatch(actionSetLocalStorageConfig({localStoreConfig: val, immediate}));
    }

    setDefaultLocalConfigStorage(): void {
        const val = this.store.getItem('ILocalStorageConfig');
        const storage = JSON.parse(val);
        if (_.isNull(storage)) {
            const v: ILocalStorageConfig = {
                studioId: Lib.RandomString(),
                noLoginUserName: '',
                sceneFilter: '',
                painterFilter: '',
                sceneCategory: '',
                painterCategory: '',
                stationFilter: '',
                dropbox_key: '',
                version,
                defaultLanguage: 'en',
                sceneOpacityLevel: '80',
                snapScale: '0.2',
                sceneGuidelinesOpacityLevel: '90',
                sceneCheckeredBackground: '1',
                fasterqEnabled: '0',
                targetPlayer: '0',
                showHelpEnabled: '1',
                advancedScheduler: '0',
                catalogEnabled: '1',
                eventsEnabled: '1',
                transitionsEnabled: '0',
                billingAsShipping: '1',
                musicEnabled: '0',
                dashboardEnabled: '0',
                resurcesEnabled: '0',
                scheduledEnabled: '1',
                paintEnabled: '1',
                localStoreConfigVersion,
                useAvatar: '0',
                theme: '0',
                largeLogo: '1',
                watchedVideoLinks: {},
                showExperienceLevel: '1',
                experienceLevel: ExperienceLevelType.BEGINNER,
                socialUserPendingStudioType: EnumStudioType.SIGN_STUDIO,
                socialUserPendingTemplateId: DEFAULT_TEMPLATE_ID,
                pwaHideInstaller: '0',
                onSaleShown: '',
                ldapServer: '',
                privateServer: '',
                saveSceneManual: '1',
                showDirections: '1',
                stickersAsEmbedded: '1',
                saveSceneTimer: '',
                isYouTubeLicense: '0',
                scriptingMode: '1',
                saveFullFeature: '0',
                restartPlayer: '0',
                restartPlayerOnSceneAssignment: '1',
                accountInitialCopyResources: '0', // | 0 = never seen any resources | -1 = not a fresh account so no need to check |  0 > still downloading
                enableDashboard: '1',
                favoriteFonts: '',
                guidelines: '',
                pageSize: '',
                monitorWallV2: '',
                isWhitelabel: this.store.getItem('IPreloginReseller') ?? '', // possibly holds a reseller id of the main enterprise master account if was passed for login?ent=12345
                dimensionIndex: '0',
                customSizeX: '',
                customSizeY: '',
                previewMode: 'size',
                previewPercent: '100',
                previewSizeX: '',
                previewSizeY: '',
                previewSceneId: '',
                rememberPreviewSettings: '0',
                zoom: '0.5',
                timelineZoom: '0.05',
                playlistScenePreviewEnabled: '1',
                recentScenePreviewEnabled: '1',
                playersFilter: '0',
                playersConnected: '0',
                playersStatsChartEnabled: '0',
                templateCategories: '',
                resourceCategories: '',
                // saveSceneOnExit: '1',
                stationMonitorUrl: '',
                equipmentPwa: '',
                equipmentUser: '',
                equipmentEmail: '',
                equipmentSlider: '',
                equipmentAccess: '',
                equipmentId: '',
                ai: { },
                slideerSidebarMinimized: '0',
                selectedColorPalette: '',
                useDevServerPort: '0',
                iotFilterRegistered: '0',
                pinnedModules: '',
                homeSectionPrefs: ''
            };
            this.setStorageConfig(v);
        } else {
            const newStorage = JSON.parse(JSON.stringify(storage));
            if (newStorage.showExperienceLevel == null) {
                newStorage.showExperienceLevel = '0';
                newStorage.experienceLevel = ExperienceLevelType.ADVANCED;
            }
            if (newStorage.watchedVideoLinks == null) {
                newStorage.watchedVideoLinks = {};
            }


            if (newStorage.ai == null) {
                newStorage.ai = {};
            }
            if (newStorage.ai.chat == null) {
                newStorage.ai.chat = aiMapping.chat.default;
            }
            if (newStorage.ai.createImage == null) {
                newStorage.ai.createImage = aiMapping.createImage.default;
            }
            if (newStorage.ai.editImage == null) {
                newStorage.ai.editImage = aiMapping.editImage.default;
            }
            if (newStorage.ai.createVideo == null) {
                newStorage.ai.createVideo = aiMapping.createVideo.default;
            }
            if (newStorage.ai.editVideo == null) {
                newStorage.ai.editVideo = aiMapping.editVideo.default;
            }
            if (newStorage.ai.autoHide == null) {
                newStorage.ai.autoHide = true;
            }

            if (newStorage.ai.showThinking == null) {
                newStorage.ai.showThinking = true;
            }

            if (newStorage.ai.lastScenePrompt == null) {
                newStorage.ai.lastScenePrompt = '';
            }

            if (newStorage.ai.sceneHistory == null) {
                newStorage.ai.sceneHistory = [];
            }

            if (newStorage.slideerSidebarMinimized == null) {
                newStorage.slideerSidebarMinimized = '0';
            }

            if (newStorage.iotFilterRegistered == null) {
                newStorage.iotFilterRegistered = '0';
            }

            if (newStorage.pinnedModules == null) {
                newStorage.pinnedModules = '';
            }

            if (newStorage.homeSectionPrefs == null) {
                newStorage.homeSectionPrefs = '';
            }

            if (JSON.stringify(storage) !== JSON.stringify(newStorage)) {
                this.setStorageConfig(newStorage);
            }
        }
    }

    setStorageConfig(val: ILocalStorageConfig): void {
        try {
            this.store.setItem('ILocalStorageConfig', JSON.stringify(val));
        } catch (e) {
            console.error('problem writing ILocalStorageConfig to local storage, out of space?', e);
            return;
        }
        this.dispatchLocalUpdated('ILocalStorageConfig');
    }

    setPreloginReseller(isWhitelabel) {
        if (isNullOrUndefOrEmpty(isWhitelabel)) return;
        this.setPreloginResellerRequest(isWhitelabel);
    }

    @once
    setPreloginResellerRequest(isWhitelabel) {
        this.store.setItem('IPreloginReseller', JSON.stringify(isWhitelabel));
        this.setStorageValConfig('isWhitelabel', isWhitelabel);
        this.storeSelector.dispatch(actionPreloginResellerRequest({isWhitelabel}));
    }

    setStorageSharedLibrary(i_sharedResourceLibrary: ISharedResourceLibrary): void {
        try {
            const newSharedResourceLibrary: ISharedResourceLibrary = {version: null, library: null};
            newSharedResourceLibrary.version = localStoreSharedLibraryVersion;
            newSharedResourceLibrary.library = i_sharedResourceLibrary.library;
            this.store.setItem('ILocalStorageSharedLibrary', JSON.stringify(newSharedResourceLibrary));
        } catch (e) {
            alert('problem accessing local storage, try clear hard disk space...');
        }

    }

    setStorageValConfig(key: string, val: any, sync = true): void {
        this.setDefaultLocalConfigStorage();
        const storage: ILocalStorageConfig = this.getStorageConfig();
        storage[key] = val;
        this.setStorageConfig(storage);
        if (key === 'theme') {
            this.changeTheme(val);
        }
        if (sync) {
            this.setRemoteLocalStorageConfig();
        }
    }

    setStorageValConfigGroup(values: Array<IConfigItem>): void {
        if (!values || !values.length) return;
        values.forEach((v) => {
            this.setStorageValConfig(v.key, v.val, false);
        });
        this.setRemoteLocalStorageConfig();
    }

    getStorageConfig(): ILocalStorageConfig {
        this.setDefaultLocalConfigStorage();
        const val = this.store.getItem('ILocalStorageConfig');
        return JSON.parse(val);
    }

    getStorageValConfig(key): any {
        this.setDefaultLocalConfigStorage();
        const storage = this.getStorageConfig();
        return storage[key];
    }

    getSharedResourceLibrary(): ISharedResourceLibrary {
        const val = this.store.getItem('ILocalStorageSharedLibrary');
        const jData = JSON.parse(val);
        if (!jData || jData?.library.length === 0 || jData?.version !== localStoreSharedLibraryVersion) return null;
        return jData;
    }

    initCartCreate(value): void {
        this.store.setItem('ICartCreate', JSON.stringify(value));
        this.dispatchLocalUpdated('ICartCreate');
    }

    getCartCreate(): ICartCreate {
        const cart: ICartCreate = this.getItem("ICartCreate");
        if (!cart) return null;
        return cart;
    }

    removeItem(key: ILocalStoreTypes): void {
        this.store.removeItem(key);
        this.dispatchLocalUpdated(key);
    }

    removeSettingsFromStorage() {
        this.store.removeItem('ILocalStorageConfig');
    }

    removeUserStorage() {
        this.store.removeItem('IUser');
        this.dispatchLocalUpdated('IUser');
    }

    removeCartStorage() {
        this.store.removeItem('ICartCreate');
        this.dispatchLocalUpdated('ICartCreate');
    }

    removeConfigStorage() {
        this.store.removeItem('ILocalStorageConfig');
        this.dispatchLocalUpdated('ILocalStorageConfig');
    }

    unCacheApp() {
        console.log('checking sw-cache');
        if ('caches' in window) {
            caches.keys()
                .then((keyList) => {
                    return Promise.all(keyList.map((key) => {
                        console.log('deleting cache ' + key);
                        return caches.delete(key);
                    }));
                });
        }
        if (window.navigator && navigator.serviceWorker) {
            navigator.serviceWorker.getRegistrations()
                .then((registrations) => {
                    for (const registration of registrations) {
                        console.log('unregistering service worker');
                        registration['unregister']();
                    }
                });
        }
    }

    clearStorage() {
        this.store.clear();
        this.dispatchLocalUpdated('CLEAR');
    }

    changeTheme(val) {
        this.theme.next(val || '0');
    }
}




